"use client"

import { useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Check, Heart, Send, Star } from "lucide-react"
import {
  enviarFeedback,
  feedbackJaEnviado,
  marcarFeedbackEnviado,
} from "@/lib/api"
import { useApp } from "@/lib/context"

const ROTULOS = [
  "Muito fraco",
  "Poderia ser melhor",
  "Razoável",
  "Muito bom!",
  "Excecional!",
]

interface FeedbackModalProps {
  pedidoId: string
  onClose: () => void
}

/** Avaliação do pedido após entrega — estrelas + comentário opcional. */
export default function FeedbackModal({ pedidoId, onClose }: FeedbackModalProps) {
  const { customerAuth } = useApp()
  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)
  const [comentario, setComentario] = useState("")
  const [aEnviar, setAEnviar] = useState(false)
  const [enviado, setEnviado] = useState(feedbackJaEnviado(pedidoId))
  const [erro, setErro] = useState<string | null>(null)

  const submeter = async () => {
    if (rating < 1) {
      setErro("Escolhe primeiro o número de estrelas.")
      return
    }
    setErro(null)
    setAEnviar(true)
    const resultado = await enviarFeedback({
      pedidoId,
      rating,
      comentario: comentario.trim(),
      nome: customerAuth?.name,
      telefone: customerAuth?.phoneNumber,
    })
    setAEnviar(false)
    if (resultado.ok) {
      marcarFeedbackEnviado(pedidoId)
      setEnviado(true)
      setTimeout(onClose, 2200)
    } else {
      setErro(resultado.message || "Tenta novamente.")
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 300 }}
          role="dialog"
          aria-modal="true"
          aria-label="Deixar feedback"
          className="w-full max-w-md rounded-t-3xl bg-card p-6 text-card-foreground shadow-2xl sm:rounded-3xl"
          onClick={(e) => e.stopPropagation()}
        >
          {enviado ? (
            <div className="flex flex-col items-center py-8 text-center">
              <motion.span
                initial={{ scale: 0.4, rotate: -12 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", damping: 14 }}
                className="flex size-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400"
              >
                <Check className="size-8" />
              </motion.span>
              <h3 className="mt-4 font-serif text-xl font-light">Obrigado!</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                O teu feedback ajuda-nos a servir melhor.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center">
                <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Heart className="size-5" />
                </span>
                <h3 className="mt-3 font-serif text-2xl font-light">Como foi a experiência?</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  A tua opinião vale ouro para a nossa cozinha.
                </p>
              </div>

              {/* Estrelas */}
              <div className="mt-6 flex justify-center gap-1.5" role="radiogroup" aria-label="Avaliação em estrelas">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={rating === n}
                    aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    className="rounded-lg p-1 transition-transform hover:scale-110 active:scale-95"
                  >
                    <Star
                      className={`size-9 transition-colors ${
                        (hover || rating) >= n
                          ? "fill-amber-400 text-amber-400"
                          : "text-muted-foreground/30"
                      }`}
                    />
                  </button>
                ))}
              </div>
              {(hover || rating) > 0 && (
                <motion.p
                  key={hover || rating}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 text-center text-sm font-medium text-primary"
                >
                  {ROTULOS[(hover || rating) - 1]}
                </motion.p>
              )}

              {/* Comentário */}
              <textarea
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Queres deixar algo por escrito? (opcional)"
                rows={3}
                maxLength={1000}
                className="mt-4 w-full resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground/60 focus:border-primary/50"
              />

              {erro && (
                <p className="mt-2 text-center text-xs text-red-500">{erro}</p>
              )}

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 rounded-full border border-border py-3 text-sm font-medium transition-colors hover:bg-muted"
                >
                  Agora não
                </button>
                <button
                  type="button"
                  disabled={aEnviar || rating < 1}
                  onClick={submeter}
                  className="flex flex-[2] items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
                  style={{ background: "linear-gradient(120deg, hsl(var(--primary)), hsl(var(--primary) / 0.85))" }}
                >
                  {aEnviar ? (
                    <span className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  ) : (
                    <Send className="size-4" />
                  )}
                  Enviar feedback
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
